import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar } from '@fortawesome/free-solid-svg-icons';

import { getPendingPayments } from '@/services/getPendingPayments';
import { deleteSettlementDocuments } from '@/services/deleteSettlementDocuments';
import { addDocumentOnSettlement } from '@/services/addDocumentOnSettlement';
import { Value } from '@/types/getPendingPaymentsType';
import { useToastNotification } from '@/hooks';

import { Button, Container } from '@/components';

import {
  PendingPaymentsFilters,
  PendingPaymentsFiltersType,
  PendingPaymentsTable,
} from './components';
import ModalPendingPayments from './ModalPendingPayments';

const PendingPayments = () => {
  const { t } = useTranslation('pendingPayments');
  const [filters, setFilters] = useState<PendingPaymentsFiltersType>({
    status: '0',
  });
  const [selectedItem, setSelectedItem] = useState<Value | undefined>();
  const [isPaymentDialogShown, setIsPaymentDialogShown] = useState(false);
  const { showToast } = useToastNotification();
  const [pendingPaymentsList, setPendingPaymentsList] = useState<Value[]>([]);
  const [paying, setPaying] = useState(false);

  const {
    mutate: getData,
    isPending: isLoading,
    data: pendingPayments,
  } = useMutation<Value[], unknown, PendingPaymentsFiltersType>({
    mutationFn: getPendingPayments,
    onSuccess: (data) => {
      setPendingPaymentsList(data);
    },
  });

  useEffect(() => {
    getData(filters);
  }, [filters, getData]);

  const handleFiltersChange = (body: PendingPaymentsFiltersType) => {
    setFilters(body);
  };

  const handleSelectionChange = (e: { value: unknown }) => {
    setSelectedItem(e?.value as Value);
  };

  const handlePreparePayment = async () => {
    if (!selectedItem) {
      showToast({
        severity: 'error',
        detail: t('selectPayment'),
      });
      return;
    }
    if (selectedItem.estado === 'En Edición') {
      try {
        setPaying(true);
        const batchSize = 20;
        await deleteSettlementDocuments(selectedItem.oid);
        const filteredLiquidaciones = pendingPaymentsList.filter(
          (item) => item.oid === selectedItem.oid
        );
        const allDocuments = filteredLiquidaciones.flatMap((liquidacion) =>
          Array.isArray(liquidacion.listaDocumentosSeleccionados)
            ? liquidacion.listaDocumentosSeleccionados.filter(
                (doc) => doc.oidDocumento
              )
            : []
        );
        for (let i = 0; i < allDocuments.length; i += batchSize) {
          const documentBatch = allDocuments.slice(i, i + batchSize);
          const promises = documentBatch.map((doc) =>
            addDocumentOnSettlement(doc.oidDocumento)
          );
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(promises);
        }
        setPaying(false);
        setIsPaymentDialogShown(true);
      } catch (error) {
        setPaying(false);
        showToast({
          severity: 'error',
          detail: t('errorDeletingSettlement'),
        });
      }
      return;
    }
    showToast({
      severity: 'error',
      detail: t('paymentIsInProcess'),
    });
  };

  const toggleIsPaymentDialog = () => {
    setIsPaymentDialogShown((prev) => !prev);
  };

  return (
    <>
      {selectedItem && isPaymentDialogShown && (
        <ModalPendingPayments
          oid={selectedItem.oid}
          isDialogShown={isPaymentDialogShown}
          toggleDialog={toggleIsPaymentDialog}
          formClientData={selectedItem}
          getData={getData}
          filters={filters}
        />
      )}

      <Container className="w-full flex-1 overflow-auto">
        <header className="w-full flex flex-column gap-3">
          <h1 className="text-primary">{t('title')}</h1>
          <PendingPaymentsFilters onChange={handleFiltersChange} />
        </header>
        <section className="w-full mt-6">
          <div className="w-full flex flex-column md:flex-row justify-content-between md:align-items-center bg-secondary-light border-round-top p-3 gap-2">
            <div className="flex gap-2">
              <Button
                className="text-primary w-auto px-3"
                type="button"
                green
                onClick={handlePreparePayment}
                disabled={paying}
              >
                <span className="text-white flex gap-2 align-items-center">
                  <FontAwesomeIcon icon={faDollar} />
                  {paying ? t('paying') : t('pay')}
                </span>
              </Button>
            </div>
          </div>
          <PendingPaymentsTable
            data={pendingPayments}
            isLoading={isLoading}
            handleSelectionChange={handleSelectionChange}
            selectedItems={selectedItem ? [selectedItem] : undefined}
            filters={filters}
            getData={getData}
          />
        </section>
      </Container>
    </>
  );
};

export default PendingPayments;
